import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { observer } from 'mobx-react-lite';
import { ElevatedButton, TextInput } from '@producer/ui-kit';

import { Select } from '../../components/select/Select';
import { updateCurrentUser, updateCurrentUserAvatar } from '../../features/user/user.slice';
import { formatPicture } from '../../core/services/image.service';
import { DescriptionHeader } from '../../features/user/components/description-header';
import { toastPool } from '../../features/toasts/models/toast-pool';
import { Toast } from '../../features/toasts/models/toast';
import { authStore } from '../../core/stores/auth-store';
import { catchError } from '../../core/catch-error';
import { ProfileAvatar } from '../../components/profile-avatar/ProfileAvatar';
import { uiStore } from '../../core/stores/ui-store';

import styles from './profile.page.module.scss';
type FormValues = {
  title: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  location: string;
};

const options = [
  { value: 'Mr', label: 'Mr' },
  { value: 'Ms', label: 'Ms' },
  { value: 'Dr', label: 'Dr' },
  { value: 'Pr', label: 'Pr' },
];

export const ProfilePage: React.FC = observer(() => {
  const { t } = useTranslation('profile');
  const user = authStore.currentUser!;

  useEffect(() => {
    uiStore.pageTitle = {
      label: 'Personal settings',
    };

    return () => {
      uiStore.pageTitle = {
        label: '',
      };
    };
  });

  const { register, control, handleSubmit } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: { ...user.toPOJO() },
  });

  const onHandleSubmit = async (values: any) => {
    const { title, firstName, lastName, phone, location } = values;

    await updateCurrentUser({ _id: user._id, title, firstName, lastName, phone, location })
      .then(() => {
        toastPool.insert(new Toast('Your settings are updated successfully'));
      })
      .catch((e) => {
        catchError(e);
      });
  };

  const handleProfilePhoto = async (file?: File) => {
    if (!file) {
      return;
    }

    const options = { width: 250, height: 250 };
    const frame = await formatPicture(file, options);
    updateCurrentUserAvatar({ file: frame }).catch((e) => {
      catchError(e);
    });
  };

  return (
    <>
      <DescriptionHeader title="Profile" description="Manage your profile" />

      <div className={styles['avatar-container']}>
        <ProfileAvatar
          placeholder="image"
          defaultUrl={user?.avatar}
          onChange={handleProfilePhoto}
        />
      </div>

      <form onSubmit={handleSubmit(onHandleSubmit)}>
        <div className={styles['field-container']}>
          <Controller
            name={'title'}
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                onSelect={({ target }) => field.onChange(target.value)}
                options={options}
                label={t('profile:title_label')}
              />
            )}
          />
        </div>

        <div className={styles['field-container']}>
          <TextInput
            {...register('firstName', { required: true })}
            placeholder={t('profile:first_name_label')}
            label={t('profile:first_name_label')}
            required
          />
        </div>

        <div className={styles['field-container']}>
          <TextInput
            {...register('lastName', { required: true })}
            placeholder={t('profile:last_name_label')}
            label={t('profile:last_name_label')}
            required
          />
        </div>

        <div className={styles['field-container']}>
          <TextInput
            {...register('email', { required: true })}
            placeholder={t('profile:last_name_label')}
            label={t('profile:email_label')}
            required
            disabled
          />
        </div>

        <div className={styles['field-container']}>
          <TextInput
            {...register('phone')}
            placeholder={t('profile:phone_label')}
            label={t('profile:phone_label')}
          />
        </div>

        <div className={styles['field-container']}>
          <TextInput
            {...register('location')}
            placeholder={t('profile:location_label')}
            label={t('profile:location_label')}
          />
        </div>

        <div className={styles['button-group-container']}>
          <ElevatedButton text="Save" type="submit" />
        </div>
      </form>
    </>
  );
});
